import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps, CSSProperties } from 'react';
import { Accordion, AccordionItem, Button } from '@carbon/react';
import { CollapseAll, ExpandAll } from '@carbon/react/icons';
import { useSetAtom, useStore } from 'jotai';
import { debounce, isEqual, isUndefined, omit, omitBy, pick } from 'lodash-es';

import { CollapsibleFilterPanel } from 'components/collapsible-filter-panel';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { RESULTS_VIEW_KEYS } from '../atoms';
import type { DatasetSearchParams } from '../types';
import { FilterForm } from './filter-form';
import { useAppliedFilterCount, useFilterCommit } from './hooks';
import styles from './filter-rail.module.scss';

const COMMIT_DEBOUNCE_MS = 300;

const RAIL_INITIAL_WIDTH_PX = 324;
const RAIL_MIN_WIDTH_PX = 208;
const RAIL_MAX_WIDTH_PX = 600;

// Set from the results toolbar (search box, Sort By, pagination), not the rail,
// so a filter commit carries the applied values through.
const RESULTS_KEYS = [...RESULTS_VIEW_KEYS, 'text'];

/**
 * A payload's filter values alone, unset ones dropped.
 * @param {Partial<DatasetSearchParams> | undefined} payload A search payload.
 * @return {object} Its filter values.
 */
function filterValues(payload: Partial<DatasetSearchParams> | undefined) {
  return omitBy(omit(payload ?? {}, RESULTS_KEYS), isUndefined);
}

type ToggleProps = Parameters<
  NonNullable<ComponentProps<typeof AccordionItem>['renderToggle']>
>[0];

interface RailSectionProps {
  category: string;
  open: boolean;
  onOpenChange: (category: string, isOpen: boolean) => void;
  /** Bumped on reset, remounting the form so templates holding their own
   *  input state (multi-selects, typeaheads) re-read the cleared filters. */
  formKey: number;
}

/**
 * One filter category's accordion section: the filter modal's own form.
 * @param {RailSectionProps} props The section.
 * @return {JSX.Element} The section.
 */
function RailSection({
  category,
  open,
  onOpenChange,
  formKey,
}: RailSectionProps) {
  // Carbon closes a section on Escape at its heading without calling
  // onHeadingClick. Kept stable so Carbon doesn't remount the heading.
  const renderToggle = useCallback(
    ({ onKeyDown, ...props }: ToggleProps) => (
      <button
        {...props}
        type="button"
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === 'Escape') onOpenChange(category, false);
        }}
      />
    ),
    [category, onOpenChange]
  );

  return (
    <AccordionItem
      title={category}
      open={open}
      onHeadingClick={({ isOpen }: { isOpen: boolean }) =>
        onOpenChange(category, isOpen)
      }
      renderToggle={renderToggle}
    >
      <FilterForm key={formKey} activeCategory={category} variant="rail" />
    </AccordionItem>
  );
}

interface RailSectionsProps {
  categories: string[];
  open: Record<string, boolean>;
  onOpenChange: (category: string, isOpen: boolean) => void;
  formKey: number;
}

/**
 * One accordion section per filter category.
 * @param {RailSectionsProps} props The sections.
 * @return {JSX.Element} The sections.
 */
function RailSectionsImpl({
  categories,
  open,
  onOpenChange,
  formKey,
}: RailSectionsProps) {
  return (
    <Accordion>
      {categories.map((category) => (
        <RailSection
          key={category}
          category={category}
          open={open[category] ?? false}
          onOpenChange={onOpenChange}
          formKey={formKey}
        />
      ))}
    </Accordion>
  );
}

// Memoised apart from the rail's width, so resizing the rail leaves the forms alone.
const RailSections = memo(RailSectionsImpl);

/**
 * The dataset's filters as a filter panel, one accordion section per filter
 * category, each the filter modal's own form. A change applies on its own (no
 * Apply step) once edits pause.
 * @param {object} props The rail.
 * @param {boolean} props.isOpen Whether the rail is showing.
 * @return {JSX.Element | null} The rail.
 */
export function FilterRail({ isOpen }: { isOpen: boolean }) {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const store = useStore();
  const { pendingFilters, seed, commit, reset } = useFilterCommit();
  const clearStagedFacets = useSetAtom(atoms.clearModalFacetsAtom);
  const filterCount = useAppliedFilterCount();
  const [formKey, setFormKey] = useState(0);
  const [width, setWidth] = useState(RAIL_INITIAL_WIDTH_PX);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const categories = useMemo(
    () => [...new Set(config.filters.map((field) => field.category))],
    [config.filters]
  );
  const allOpen = categories.every((category) => open[category]);

  // Seeded once, from the search applied at mount.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    seed();
  }, [seed]);

  // Applies the reader's edits once they pause. Read when it fires, the applied
  // search supplies what the results toolbar set meanwhile (a sort, a search),
  // and staged filters that already match it are not an edit. The options then
  // read the committed counts again, dropping any typeahead fetch in flight.
  const commitEdits = useMemo(
    () =>
      debounce((staged: Partial<DatasetSearchParams>) => {
        const applied =
          store.get(atoms.searchAtom).currentPayload ?? config.defaultPayload;
        if (isEqual(filterValues(staged), filterValues(applied))) return;
        commit({ ...staged, ...pick(applied, RESULTS_KEYS) });
        clearStagedFacets();
      }, COMMIT_DEBOUNCE_MS),
    [store, atoms.searchAtom, config.defaultPayload, commit, clearStagedFacets]
  );

  useEffect(() => () => commitEdits.cancel(), [commitEdits]);

  useEffect(() => {
    commitEdits(pendingFilters);
  }, [pendingFilters, commitEdits]);

  const onOpenChange = useCallback(
    (category: string, isSectionOpen: boolean) =>
      setOpen((current) => ({ ...current, [category]: isSectionOpen })),
    []
  );

  return (
    <CollapsibleFilterPanel
      filterCount={filterCount}
      onReset={() => {
        reset();
        setFormKey((key) => key + 1);
      }}
      isOpen={isOpen}
      // Date pickers render their calendars in the body, so the panel can
      // scroll its own overflow.
      scrollable
      stickyHeader
      width={width}
      minWidth={RAIL_MIN_WIDTH_PX}
      maxWidth={RAIL_MAX_WIDTH_PX}
      onResize={setWidth}
      headerActions={
        <Button
          kind="ghost"
          size="lg"
          hasIconOnly
          renderIcon={allOpen ? CollapseAll : ExpandAll}
          iconDescription={
            allOpen ? 'Collapse all filters' : 'Expand all filters'
          }
          tooltipPosition="bottom"
          tooltipAlignment="end"
          onClick={() =>
            setOpen(
              Object.fromEntries(
                categories.map((category) => [category, !allOpen])
              )
            )
          }
        />
      }
    >
      <div
        className={styles.rail}
        // The open menus float free of the rail but still size to its fields.
        style={{ '--rail-width': `${width}px` } as CSSProperties}
      >
        <RailSections
          categories={categories}
          open={open}
          onOpenChange={onOpenChange}
          formKey={formKey}
        />
      </div>
    </CollapsibleFilterPanel>
  );
}
